<template>
  <div v-if="facets.length"
    class="spec-filters">
    <div v-for="f in facets"
      :key="f.specDefinitionId"
      :class="['spec-filter', 'spec-filter--' + f.type]">
      <template v-if="f.type === 'chips'">
        <span class="spec-filter__label">{{ label(f) }}</span>
        <div class="spec-filter__chips">
          <button v-for="v in f.values"
            :key="v.value"
            type="button"
            :class="['spec-chip', { 'is-on': isOn(f, v.value) }]"
            :disabled="!v.count && !isOn(f, v.value)"
            @click="toggle(f, v.value)">
            {{ v.value }}<span class="spec-chip__count">{{ v.count }}</span>
          </button>
        </div>
      </template>

      <div v-else
        class="spec-dropdown"
        :class="{ 'is-open': open === f.specDefinitionId }">
        <button type="button"
          :class="['spec-dropdown__toggle', { 'is-on': chosen(f).length }]"
          @click.stop="open = open === f.specDefinitionId ? null : f.specDefinitionId">
          <span class="spec-dropdown__name">{{ label(f) }}</span>
          <span v-if="chosen(f).length"
            class="spec-dropdown__value">{{ chosen(f).join(', ') }}</span>
          <i class="fa-solid fa-chevron-down" />
        </button>
        <div v-if="open === f.specDefinitionId"
          class="spec-dropdown__panel"
          @click.stop>
          <CheckBox v-for="v in f.values"
            :key="v.value"
            :class="['spec-dropdown__option', { 'is-empty': !v.count && !isOn(f, v.value) }]"
            :model-value="isOn(f, v.value)"
            :disabled="!v.count && !isOn(f, v.value)"
            @update:model-value="toggle(f, v.value)"><span>{{ v.value }}</span><span class="spec-chip__count">{{ v.count }}</span></CheckBox>
        </div>
      </div>
    </div>

    <button v-if="anyOn"
      type="button"
      class="spec-filters__clear"
      @click="$emit('clear')">
      <i class="fa-solid fa-xmark" /> Tühjenda filtrid
    </button>
  </div>
</template>

<script>
// A category's spec filters: chips for short lists, a checkbox dropdown for long ones. Counts are products.
export default {
  name: 'spec-filters',
  props: {
    // From GET /categories/:id/filters.
    facets: { type: Array, default: () => [] },
    // Spec definition id → chosen values.
    selected: { type: Object, default: () => ({}) },
  },
  emits: ['change', 'clear'],
  data() {
    return { open: null }
  },
  computed: {
    anyOn() {
      return Object.values(this.selected).some((v) => v?.length)
    },
  },
  mounted() {
    document.addEventListener('click', this.close)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.close)
  },
  methods: {
    close() {
      this.open = null
    },
    label(f) {
      return f.unit ? `${f.name} (${f.unit})` : f.name
    },
    chosen(f) {
      return this.selected[f.specDefinitionId] || []
    },
    isOn(f, value) {
      return this.chosen(f).includes(value)
    },
    toggle(f, value) {
      const now = this.chosen(f)
      this.$emit('change', f.specDefinitionId, now.includes(value) ? now.filter((v) => v !== value) : [...now, value])
    },
  },
}
</script>

<style lang="scss" scoped>
.spec-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12rem 20rem;
  margin: -8rem 0 24rem;
  padding: 14rem 16rem;
  border: 1rem solid #e8edf0;
  border-radius: 4rem;
  background: #fff;
}

.spec-filter--chips {
  display: flex;
  flex-direction: column;
  gap: 6rem;
}

.spec-filter__label {
  font-size: 12rem;
  font-weight: 600;
  letter-spacing: 1rem;
  text-transform: uppercase;
  color: #6b7881;
}

.spec-filter__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4rem;
}

.spec-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 4rem;
  padding: 4rem 9rem;
  border: 1rem solid #dfe5e9;
  border-radius: 14rem;
  background: #fff;
  color: #333;
  font-size: 12.5rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--primary);
  }

  &.is-on {
    background: var(--primary);
    border-color: var(--primary);
    color: #fff;

    .spec-chip__count {
      color: rgba(255, 255, 255, 0.75);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.spec-chip__count {
  color: #9aa6ae;
  font-size: 10.5rem;
  font-variant-numeric: tabular-nums;
}

.spec-dropdown {
  position: relative;
  align-self: flex-end;
}

.spec-dropdown__toggle {
  display: inline-flex;
  align-items: center;
  gap: 8rem;
  max-width: 260rem;
  padding: 7rem 12rem;
  border: 1rem solid #dfe5e9;
  border-radius: 4rem;
  background: #fff;
  color: #333;
  font-size: 13rem;
  cursor: pointer;

  i {
    font-size: 10rem;
    color: #9aa6ae;
    transition: transform 0.15s;
  }

  &.is-on {
    border-color: var(--primary);
  }
}

.spec-dropdown.is-open .spec-dropdown__toggle i {
  transform: rotate(180deg);
}

.spec-dropdown__name {
  white-space: nowrap;
}

.spec-dropdown__value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  color: var(--primary);
}

.spec-dropdown__panel {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4rem);
  left: 0;
  min-width: 100%;
  max-height: 300rem;
  overflow-y: auto;
  padding: 6rem 0;
  border: 1rem solid #e8edf0;
  border-radius: 4rem;
  background: #fff;
  box-shadow: 0 8rem 24rem rgba(0, 0, 0, 0.12);
}

.spec-dropdown__option {
  display: flex;
  align-items: center;

  :deep(.check__text) {
    display: flex;
    flex: 1;
    gap: 8rem;
  }

  gap: 8rem;
  padding: 5rem 14rem;
  font-size: 13rem;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: #f6f8f9;
  }

  .spec-chip__count {
    margin-left: auto;
    padding-left: 12rem;
  }

  &.is-empty {
    opacity: 0.4;
    cursor: default;
  }
}

.spec-filters__clear {
  align-self: flex-end;
  padding: 7rem 0;
  border: none;
  background: none;
  color: var(--accent);
  font-size: 13rem;
  cursor: pointer;
}
</style>
